import type { DiscoverUserGetResponse } from "libs/apiClient"
import { ContentCell } from "./ContentCell";
import client from "libs/client";
import Modal from "../../../../../assets/js/components/Modal";
import { ActivateDeactivateModal } from "features/discover/ActivateDeactivateModal";
import { UserPermissionsTable } from "features/discover/UserPermissionsTable";
import { Link } from "react-router";
import React, { useState, useId } from "react";
import { SecurityCodesTable } from "features/discover/SecurityCodesTable";
import { Tabs } from "features/discover/Tabs";
import { PlayerSelectGridModal } from "features/discover/PlayerSelectGridModal";
import "./admin-ui.scss";
import { DiscoverUserGuardianApprovalsTable } from "features/discover/DiscoverUserGuardianApprovalsTable";
import { useToast } from "ui-kit";
import { _ } from "i18n";
import type { Keys } from "i18n/locales/types";
import { formatDate, formatDateTime } from "features/discover/functions";
import { UserNotesTable } from "./UserNotesTable";
import { UserNoteForm } from "./UserNoteForm";

interface DiscoverUserDetailProps {
	userData: DiscoverUserGetResponse;
	showModal: boolean;
	setShowModal: (showModal: boolean) => void;
}

export function DiscoverUserDetail({ userData, showModal, setShowModal }: DiscoverUserDetailProps) {
	const [tab, setTab] = useState<string | null>("permissions");
	const [showAddPermissionModal, setShowAddPermissionModal] = useState<boolean>(false);
	const [permissionModalSize, setPermissionModalSize] = useState<"sm" | "full">("full");
	const { addSuccessToast, addErrorToast } = useToast();
	const [cellsWithEditIcon, setCellsWithEditIcon] = useState<Set<string>>(new Set());
	const [cellsInEditMode, setCellsInEditMode] = useState<Set<string>>(new Set());
	const [showAddNoteModal, setShowAddNoteModal] = useState<boolean>(false);

	const contentProps = {
		id: userData.id,
		mutation: client.patchDiscoverUser.useMutation(),
		invalidationKey: 'discoverUserDetail',
	};

	const copyToClipboard = async (text: string) => {
		try {
			await navigator.clipboard.writeText(text);
			addSuccessToast("Copied to clipboard");
		} catch (err) {
			addErrorToast("Failed to copy to clipboard");
		}
	};

	const extractTextFromReactNode = (node: React.ReactNode): string => {
		if (typeof node === 'string' || typeof node === 'number') {
			return String(node);
		}
		if (typeof node === 'object' && node !== null) {
			if ('props' in node && node.props) {
				const children = node.props.children;
				if (children) {
					if (Array.isArray(children)) {
						return children.map(extractTextFromReactNode).filter(Boolean).join(', ');
					}
					return extractTextFromReactNode(children);
				}
			}
		}
		return String(node) || '-';
	};

	const getValueToCopy = (props: { type: string; value?: string | number | boolean | null | undefined; formatter?: (value: string | number | boolean | null | undefined) => string | React.ReactNode }) => {
		if (props.formatter) {
			const formatted = props.formatter(props.value);
			if (typeof formatted === 'string') {
				return formatted;
			}
			// For ReactNode, extract text content recursively
			return extractTextFromReactNode(formatted);
		}

		if (props.type === "date") {
			return formatDate(props.value as string | null);
		}

		if (props.type === "datetime") {
			return formatDateTime(props.value as string | null);
		}

		return props.value?.toString() || "-";
	};

	const handleCellClick = (e: React.MouseEvent, props: { field?: string; type: string; value?: string | number | boolean | null | undefined; formatter?: (value: string | number | boolean | null | undefined) => string | React.ReactNode }, cellKey: string) => {
		// Don't copy if clicking on buttons, inputs, or the edit icon
		const target = e.target as HTMLElement;
		if (target.tagName === 'BUTTON' || target.tagName === 'INPUT' || target.tagName === 'IMG' || target.closest('button') || target.closest('input') || target.closest('img')) {
			return;
		}

		// Stop propagation to prevent ContentCell's onClick
		e.stopPropagation();

		if (props.field) {
			const valueToCopy = getValueToCopy(props);
			copyToClipboard(valueToCopy);
			// Show edit icon for this cell
			setCellsWithEditIcon(prev => new Set(prev).add(cellKey));
		}
	};

	const handleEditIconClick = (e: React.MouseEvent, cellKey: string) => {
		e.stopPropagation();
		setCellsInEditMode(prev => new Set(prev).add(cellKey));
		setCellsWithEditIcon(prev => {
			const newSet = new Set(prev);
			newSet.delete(cellKey);
			return newSet;
		});
	};

	const handleEditChange = (cellKey: string, isEdit: boolean) => {
		if (isEdit) {
			setCellsInEditMode(prev => new Set(prev).add(cellKey));
		} else {
			setCellsInEditMode(prev => {
				const newSet = new Set(prev);
				newSet.delete(cellKey);
				return newSet;
			});
			setCellsWithEditIcon(prev => {
				const newSet = new Set(prev);
				newSet.delete(cellKey);
				return newSet;
			});
		}
	};

	const birthdayTitle = (): string => {
		let title = "Birthday";

		if (userData.birthday !== null) {
			if (userData.isMinor) {
				title += " (minor)";
			} else {
				title += " (adult)";
			}
		}

		return title;
	}

	const securityCodes = userData?.passwordSecurityCodes || [];

	const tempId1 = useId();
	const tempId2 = useId();

	const basicProps = [
		{
			key: useId(),
			title: "First Name",
			field: "firstName",
			type: "string",
			value: userData.firstName,
		},
		{
			key: useId(),
			title: "Last Name",
			field: "lastName",
			type: "string",
			value: userData.lastName,
		},
		{
			key: useId(),
			title: "E-mail",
			field: "email",
			type: "string",
			value: userData.email,
		},
		{
			key: useId(),
			title: "Phone Number",
			field: "phoneNumber",
			type: "string",
			value: userData.phoneNumber,
		},
		{
			key: useId(),
			title: birthdayTitle(),
			field: "birthday",
			type: "date",
			value: userData.birthday,
		},
		{
			key: useId(),
			title: "Country",
			type: "string",
			value: userData.country?.name,
		},
		{
			key: useId(),
			title: "Created at",
			type: "datetime",
			value: userData.createdAt,
		},
		{
			key: useId(),
			title: "Is active",
			type: "boolean",
			value: userData.isActive,
		},
		{
			key: useId(),
			title: "Roles",
			type: "string",
			formatter: () => <div>
				{userData.roles.map((r, index) => <div key={`role-${index}`}>{r}</div>)}
			</div>,
		},
		{
			key: useId(),
			title: "Pending removal",
			field: "pendingRemoval",
			type: "boolean",
			value: userData.pendingRemoval,
		},
		// only display if pending removal is true
		...(userData.pendingRemoval ? [{
			key: tempId1,
			title: "Pending removal reason (optional)",
			field: "pendingRemovalReason",
			type: "string",
			value: userData.pendingRemovalReason,
		}] : []),
		...(userData.pendingRemoval ? [{
			key: tempId2,
			title: "Pending removal at",
			type: "datetime",
			value: userData.pendingRemovalAt,
		}] : []),
		{
			key: useId(),
			title: "Is Enterprise",
			type: "boolean",
			field: "isEnterprise",
			value: userData.isEnterprise,
		}
	];

	const platformRelationsProps = [
		{
			key: useId(),
			title: "Club pro",
			type: "string",
			formatter: () => userData.clubPro ? <Link to={`/admin/club-pro/${userData.clubPro.id}`} className="button-link">{userData.clubPro.name} ({userData.clubPro.id})</Link> : '-',
		},
		{
			key: useId(),
			title: "Club Partner",
			type: "string",
			formatter: () => userData.clubAmateur ? <Link to={`/admin/club-amateur/${userData.clubAmateur.id}`} className="button-link">{userData.clubAmateur.name} ({userData.clubAmateur.id})</Link> : '-',
		},
		{
			key: useId(),
			title: "Team",
			type: "string",
			formatter: () => userData.clubAmateur && userData.team ? <Link to={`/club/${userData.clubAmateur.id}/teams#${userData.team.id}`} className="button-link">{userData.team.name} ({userData.team.id})</Link> : '-',
		},
		{
			key: useId(),
			title: "Player",
			type: "string",
			formatter: () => userData.player ? <Link to={`/discover/player-data/${userData.player.metadata['playerDataId'] || ''}`} className="button-link">{userData.player.name} ({userData.player.id})</Link> : '-',
		},
	];

	const tabs = [
		{
			label: _("discover.userDetail.tab.permissions" as keyof Keys),
			key: "permissions",
		}, {
			label: _("discover.userDetail.tab.securityCodes" as keyof Keys),
			key: "securityCodes",
			disabled: securityCodes.length === 0
		}, {
			label: _("discover.userDetail.tab.guardianApprovals" as keyof Keys),
			key: "guardianApprovals",
			disabled: userData.userGuardianApprovalsCount < 1,
		},
		{
			label: _("discover.userNote.tab" as keyof Keys),
			key: "notes",
			disabled: userData.canAccessUserNotes === false,
		},
	];

	return (
        <>
			<div className="discover-card__title">Basic user data</div>
			<div className="discover-card">
				<div className="cell-grid">
					{basicProps.map((props, index) => {
						const cellKey = props.field || `cell-${index}`;
						const showEditIcon = !!(cellsWithEditIcon.has(cellKey) && props.field && !cellsInEditMode.has(cellKey));
						const isEdit = cellsInEditMode.has(cellKey);

						return (
							<div key={cellKey} onClick={(e) => handleCellClick(e, props, cellKey)}>
								<ContentCell
									{...contentProps}
									{...props}
									externalEditControl={!!props.field}
									isEdit={isEdit}
									onEditChange={(edit) => handleEditChange(cellKey, edit)}
									showEditIcon={showEditIcon}
									onEditIconClick={(e) => handleEditIconClick(e, cellKey)}
								/>
							</div>
						);
					})}
				</div>
			</div>

			{!!userData?.hasPortal && <>
				<div className="discover-card__title">Platform relations</div>
				<div className="discover-card">
					<div className="cell-grid">
						{platformRelationsProps.map((props) => {
							return <ContentCell {...contentProps} {...props} />
						})}
					</div>
				</div>
			</>}

			<Tabs items={tabs} active={tab} onClick={(key) => setTab(key)} />

			{tab === "permissions" && <div className="discover-card">
				<UserPermissionsTable userPermissions={userData?.userPermissions || []} deletable={true} />
				<div className="admin-buttons admin-buttons--right">
					<button
						className="admin-button admin-button--primary"
						onClick={() => {
							setShowAddPermissionModal(true)
							setPermissionModalSize("full")
						}}
					>
						Add player permission
					</button>

				</div>
				<Modal show={showAddPermissionModal} size={permissionModalSize} title="Choose player" position="center" onClose={() => {
					setPermissionModalSize("full");
					setShowAddPermissionModal(false);
				}}>
					<PlayerSelectGridModal
						setPermissionModalSize={setPermissionModalSize}
						setShowAddPermissionModal={setShowAddPermissionModal}
						userId={userData.id}
					/>
				</Modal>
			</div>}

			{tab === "securityCodes" && <div className="discover-card">
				<SecurityCodesTable securityCodes={userData?.passwordSecurityCodes || []} />
			</div>}

			{tab === "guardianApprovals" && <div className="discover-card">
				<DiscoverUserGuardianApprovalsTable
					columnVisibility={{
						user: false,
					}}
					defaultFilters={{
						user: userData.id,
					}}
				/>
			</div>}

			{tab === "notes" && <div className="discover-card">
				<UserNotesTable userNotes={userData?.userNotes || []} />
				<div className="admin-buttons admin-buttons--right">
					<button
						className="admin-button admin-button--primary"
						onClick={() => {
							setShowAddNoteModal(true)
						}}
					>
						{_("discover.userNote.addNote")}
					</button>
				</div>
				<Modal show={showAddNoteModal} size="sm" title={_("discover.userNote.addNote")} position="top" onClose={() => {
					setShowAddNoteModal(false)
				}}>
					<UserNoteForm userId={userData.id} mode="create" setShowModal={setShowAddNoteModal} />
				</Modal>
			</div>}

			<Modal show={showModal} onClose={() => setShowModal(false)} size="sm" title={userData.isActive ? "Deactivate" : "Activate"} position="center">
				{userData.isActive !== undefined && userData.id &&
					<ActivateDeactivateModal id={userData.id} isActive={userData.isActive} close={() => setShowModal(false)} />}
			</Modal>
		</>
	)
}
